/**
 * The ONE place a Clerk identity becomes a Frappe session.
 *
 * Clerk proves who someone is to Bosun; Frappe's `sid` cookie is what authorises every
 * `quoflow.*` call and the realtime socket. This module converts the first into the second by
 * posting a Clerk-signed JWT to `auth.session_from_clerk`, which verifies it against Clerk's
 * JWKS and calls `login_as`. See the design doc for why this shape was chosen over making
 * Frappe an OAuth client of Clerk (short version: this one recovers from an expired session
 * without a full-page redirect, so in-progress edits on /compare/:id survive).
 *
 * Clerk is INJECTED, not imported — `setClerkTokenSource` follows the same pattern as
 * `client.ts`'s `setActingRole`. That keeps `src/lib/api/` free of Clerk entirely, and it is
 * what makes every function here testable with a plain stub in a repo with no component-test
 * infrastructure.
 *
 * This module must never be reached from the retry wrappers it feeds — the exchange call below
 * uses a bare `fetch`, not `bosun.ts`'s `send`, precisely so a failing exchange cannot recurse.
 *
 * CSRF (`docs/bosun-frappe-asks.md` `A3b`, delivered 2026-08-11): the exchange response carries a
 * `csrf_token`, which this module captures (`setCsrfToken`) and exposes (`currentCsrfToken`). The
 * write transports (`bosun.ts`, `frappe.ts`, `client.ts`) and `signOut.ts` attach it as
 * `X-Frappe-CSRF-Token` on every cookie-authenticated write. In dev the exchange never runs (a
 * proxy-injected API token authorises calls and bypasses CSRF), so `currentCsrfToken()` stays
 * `null` and no header is sent — the whole mechanism is inert there.
 *
 * ---- Why the exchange has to recover from its OWN CSRF rejection ----
 *
 * This header used to claim the exchange `POST` needs no CSRF token because "it runs as Guest (no
 * `sid` yet)". That is true on a FIRST sign-in and false on every page refresh afterwards, which
 * is the whole bug (confirmed against rfqdev, 2026-08-13):
 *
 *   - the `sid` cookie survives a reload — it is a cookie;
 *   - `csrfToken` below does NOT — it is module state, wiped by the reload.
 *
 * So the exchange re-runs as an AUTHENTICATED POST carrying no `X-Frappe-CSRF-Token`, Frappe
 * answers `CSRFTokenError`, and `FrappeSessionGate` shows "We couldn't complete sign-in" — every
 * refresh, until the user clears cookies by hand.
 *
 * Recovery has to get us back to Guest, and in a production build the browser is CROSS-ORIGIN to
 * Frappe (`apiBase()` points at the bench; there is no proxy), so no JS here can read or delete
 * that cookie. The only lever left is asking Frappe to clear it for us — see `LOGOUT_URL` below
 * for which of its two logout routes actually works, and the measurements behind that.
 *
 * This is a WORKAROUND for a small server-side omission, not the fix — filed as `A3d` in
 * `docs/bosun-frappe-asks.md`. When that ships the first exchange stops failing, the recovery path
 * below stops being reached, and it can be deleted wholesale.
 */

import { apiBase } from './apiBase'
import { currentAuthProvider } from './authProvider'
import type { AuthProvider } from '../auth'
import { BosunApiError } from './errors'
import { assertOk, methodUrl } from './transport'
import { parseCurrentUser, type CurrentUser } from './currentUser'

/** How long the exchange waits before giving up and falling through to the unreachable screen. */
const EXCHANGE_TIMEOUT_MS = 15_000

export type ClerkTokenSource = () => Promise<string | null>

let tokenSource: ClerkTokenSource | null = null

/** Installed once by `FrappeSessionGate`. `null` clears it (tests, and sign-out). */
export function setClerkTokenSource(fn: ClerkTokenSource | null): void {
  tokenSource = fn
}

/**
 * Whether a Clerk token source is installed. Every retry path checks this so the whole
 * mechanism is inert in the keyless dev mode, where the gate is never mounted and there is
 * nothing to exchange.
 */
export function hasClerkTokenSource(): boolean {
  return tokenSource !== null
}

/**
 * The Clerk JWT to present to the PRODUCTION PROXY as `Authorization: Bearer`, or `null`.
 *
 * Deployed, `server/index.ts` is what the browser talks to: it verifies this token against Clerk's
 * JWKS, resolves the caller's Frappe role, and only then forwards under the service token. So in a
 * deployed build this header is the entire proof of identity — there is no session cookie at all,
 * which is exactly why `A3d`'s CSRF failure cannot happen there.
 *
 * `import.meta.env.DEV` gates it, and the gate is NOT cosmetic. In dev there is no proxy: Vite
 * forwards `/api` straight to Frappe, which would try to interpret this as one of its own bearer
 * credentials, fail, and answer `AuthenticationError` — breaking local development in a way that
 * looks like a Frappe problem. Dev authenticates with the `sid` cookie; production with this.
 *
 * Never throws: a Clerk refresh that fails must degrade to "no header" (and a clean 401 from the
 * proxy), not reject inside every transport in the app.
 */
export async function currentClerkToken(): Promise<string | null> {
  if (import.meta.env.DEV || !tokenSource) return null
  try {
    return await tokenSource()
  } catch {
    return null
  }
}

/**
 * The bearer for `/api`, which is `currentClerkToken` ONLY when `/api` is our own server.
 *
 * `apiBase()` is the test, and it is exact: empty means same-origin, i.e. requests reach
 * `server/index.ts`, which is what verifies this token. A non-empty base means the deployed build
 * addresses Frappe DIRECTLY (search-only mode, and the static build before it) — and Frappe would
 * try to interpret a Clerk JWT as one of its own credentials, fail, and answer
 * `AuthenticationError`. So this must be `null` there, and the `sid` cookie authenticates instead.
 *
 * Sending it anyway would also hand a valid Clerk token to a server that has no business holding
 * one, which is the more serious half of getting this wrong.
 */
export async function proxyBearer(): Promise<string | null> {
  return proxyBearerFor(currentAuthProvider())
}

/**
 * The same decision with the provider named, so it can be tested without stubbing the module's
 * own env read. Under `frappe` there is NO bearer, ever: Frappe checks `Authorization` before the
 * cookie and does not fall back, so a token here overrides a perfectly good session and every
 * call comes back `AuthenticationError` — the footgun `vite.config.ts` documents (2026-09-24).
 */
export async function proxyBearerFor(provider: AuthProvider): Promise<string | null> {
  if (provider === 'frappe') return null
  if (apiBase() !== '') return null
  return currentClerkToken()
}

/** The exchange currently in flight, if any. */
let inFlight: Promise<CurrentUser> | null = null

/** The CSRF token Frappe returns in the exchange payload (`A3b`). Held in memory only — never
 * stored — and attached as `X-Frappe-CSRF-Token` on every cookie-authenticated write. `null`
 * until the first exchange; cleared on sign-out. In dev with a proxy-injected API token this is
 * never populated (the exchange is not run) and writes bypass CSRF anyway. */
let csrfToken: string | null = null

export function currentCsrfToken(): string | null {
  return csrfToken
}

export function setCsrfToken(token: string | null): void {
  csrfToken = token
}

/**
 * Obtain a Frappe session for the signed-in Clerk user, returning who the server says we are.
 *
 * Single-flight: concurrent callers share one exchange. A screen firing six parallel queries
 * that all 401 must trigger ONE exchange, not six — six would race, and five would be wasted
 * round-trips against an endpoint that mints sessions.
 */
export function ensureFrappeSession(): Promise<CurrentUser> {
  if (inFlight) return inFlight
  const run = exchange().finally(() => {
    // Only clear if we are still the current attempt, so a later call cannot be cancelled by an
    // earlier one settling.
    if (inFlight === run) inFlight = null
  })
  inFlight = run
  return run
}

/** Frappe's `exc_type` when an authenticated unsafe request arrives with no CSRF token. */
const CSRF_EXC_TYPE = 'CSRFTokenError'

/**
 * Frappe's WEBSITE logout route (`frappe/www/logout.py`), deliberately NOT the API method
 * `signOut.ts` uses.
 *
 * `/api/method/logout` is unusable here, both ways round — measured on the bench, 2026-08-13:
 *
 *   - as a POST it is CSRF-checked, i.e. blocked by the very error we are recovering from;
 *   - as a GET it is refused by `is_valid_http_method` (`PermissionError: Not permitted`, thrown
 *     from `frappe/handler.py`) because the method is declared POST-only. An earlier version of
 *     this file guessed that GET 403 was a Guest permission failure. It was not, and the fix
 *     built on that guess did not work.
 *
 * `/logout` is a website page, not a whitelisted method, so it has no such method restriction: it
 * answers GET 200 and responds `Set-Cookie: sid=Guest` (plus `user_id`/`full_name`), which is
 * precisely the state we need to be in. It is `SameSite=None; Secure`, so the browser accepts it
 * cross-site, and the bench returns `Access-Control-Allow-Origin` for our deployed origin with
 * `Access-Control-Allow-Credentials: true` — both verified before this was written.
 */
const LOGOUT_URL = '/logout'

type ExchangeResponse = {
  status: number
  statusText: string
  ok: boolean
  parsed: unknown
  parseFailed: boolean
}

/** One exchange attempt: fresh Clerk token, one POST, the raw response — no throwing on status,
 * so the caller can inspect `exc_type` and decide whether recovery is worth a second attempt. */
async function postExchange(): Promise<ExchangeResponse> {
  // FRESH every time, including on the retry below. Clerk session JWTs are short-lived (60s by
  // default); caching one and replaying it on a 401 twenty minutes later fails verification —
  // intermittently, which is the worst kind of bug to chase.
  const token = await tokenSource!()
  if (!token) {
    throw new BosunApiError(401, 'BosunInvalidToken', ['No Clerk session token available'], '401')
  }

  const res = await fetch(apiBase() + methodUrl('auth', 'session_from_clerk'), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
    // Without this, a hung connection leaves the gate on "Signing you in…" forever — that screen
    // has no button, so there is no way out short of reloading the tab. `AbortSignal.timeout`
    // rejects with a status-less `AbortError`, which `classifyGateError` already buckets as
    // 'unreachable' (no `status` on the error) — the same screen a CORS failure produces, and the
    // one screen here that offers "Try again".
    signal: AbortSignal.timeout(EXCHANGE_TIMEOUT_MS),
  })

  let parsed: unknown = null
  let parseFailed = false
  try {
    parsed = await res.json()
  } catch {
    parseFailed = true
  }
  return { status: res.status, statusText: res.statusText, ok: res.ok, parsed, parseFailed }
}

function isCsrfRejection(parsed: unknown): boolean {
  return (parsed as { exc_type?: unknown } | null)?.exc_type === CSRF_EXC_TYPE
}

/**
 * End the Frappe session whose cookie is blocking the exchange, so the next attempt is Guest.
 *
 * GET, not POST: CSRF guards the unsafe methods, so a POST logout fails with the same error we
 * are here to recover from. `credentials: 'include'` is required in both directions — to send the
 * stale `sid`, and to store the `Set-Cookie` that clears it.
 *
 * Best effort. If it fails, the retry simply hits the same rejection and the original error
 * reaches the gate, which is exactly today's behaviour — no worse for having tried.
 */
async function dropStaleFrappeSession(): Promise<void> {
  try {
    await fetch(apiBase() + LOGOUT_URL, {
      credentials: 'include',
      signal: AbortSignal.timeout(EXCHANGE_TIMEOUT_MS),
    })
  } catch {
    // Deliberately swallowed — see above.
  }
}

async function exchange(): Promise<CurrentUser> {
  if (!tokenSource) {
    throw new Error('ensureFrappeSession called with no Clerk token source installed')
  }

  let res = await postExchange()

  // Retry ONCE, and only for CSRF. A second rejection is a real failure, not something another
  // logout would clear — anything more would be a loop against an endpoint that mints sessions.
  if (isCsrfRejection(res.parsed)) {
    await dropStaleFrappeSession()
    res = await postExchange()
  }

  if (res.parseFailed && !res.ok) {
    throw new BosunApiError(res.status, undefined, [], `${res.status} ${res.statusText}`)
  }
  assertOk(res.status, res.parsed, res.statusText)

  const message = (res.parsed as { message?: { csrf_token?: unknown } } | null)?.message
  const csrf = typeof message?.csrf_token === 'string' ? message.csrf_token : null
  setCsrfToken(csrf)

  const user = parseCurrentUser(message)
  if (!user) {
    throw new BosunApiError(
      res.status,
      'BosunInvalidToken',
      ['The session response carried no user email'],
      'malformed session response',
    )
  }
  return user
}

/** Forget any in-flight exchange. Called on sign-out; the `sid` cookie itself is killed by
 * `POST /api/method/logout`, which this module does not own. */
export function clearFrappeSession(): void {
  inFlight = null
  csrfToken = null
}
